import { createContext, type PropsWithChildren, Suspense, use, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { FrameLoadingTrigger, useLoaderManager } from '@interxfi/design-system/components/frame';
import { LayoutMeasurementContextProvider, useLayoutMeasurementContext } from '@interxfi/design-system/util/layout-measurement-context';
import { debounce } from '@interxfi/utilities/fn/debounce';
import { RealmPageBanner } from '../realm/banner/realm-page-banner';
import { useRealmIndicatorContext } from '../realm/realm-indicator-context';
import { LayoutContextProvider, useLayoutContext } from './context';
import { Sidebar } from './sidebar/sidebar';
import { Topbar } from './topbar/topbar';
import styles from './layout.module.scss';

const UniqueLayoutContext = createContext(false);

interface LayoutProps {
  // bare is for rendering the layout without a sidebar, and with only the user settings in the top bar
  bare?: boolean;
}

function InnerLayout({ bare, children }: PropsWithChildren<LayoutProps>) {
  const { setTopbarHeight } = useLayoutMeasurementContext();
  const [bannerRef, setBannerRef] = useState<HTMLDivElement | null>(null);
  const [topbarRef, setTopbarRef] = useState<HTMLElement | null>(null);
  const { collapsedSidebarStatus, setSidebarRef, isMobile, isMobileNavExpanded, showCollapsedSidebar } = useLayoutContext();
  const { showBanner } = useRealmIndicatorContext();
  const { loadingStack } = useLoaderManager();
  const [maxLoadingItems, setMaxLoadingItems] = useState(0);
  const [isWaitingForDebounce, setIsWaitingForDebounce] = useState(false);
  const isLoading = loadingStack > 0;
  const debouncedSetMaxLoadingItems = useMemo(
    () =>
      debounce((items: number) => {
        setMaxLoadingItems(items);
        setIsWaitingForDebounce(false);
      }, 100),
    [],
  );

  useEffect(() => {
    if (loadingStack > maxLoadingItems) {
      setIsWaitingForDebounce(true);
      debouncedSetMaxLoadingItems(loadingStack);
    }

    if (loadingStack === 0) {
      setMaxLoadingItems(0);
      setIsWaitingForDebounce(false);
    }
  }, [debouncedSetMaxLoadingItems, maxLoadingItems, loadingStack]);

  useLayoutEffect(() => {
    if (!bannerRef && !topbarRef) {
      return;
    }

    function handleResize() {
      if (!bannerRef && !topbarRef) {
        return;
      }

      setTopbarHeight((bannerRef?.offsetHeight || 0) + (topbarRef?.offsetHeight || 0));
    }

    const observer = new ResizeObserver(handleResize);

    if (bannerRef) {
      observer.observe(bannerRef);
    }

    if (topbarRef) {
      observer.observe(topbarRef);
    }

    handleResize();

    return () => {
      observer.disconnect();
    };
  }, [bannerRef, topbarRef, setTopbarHeight]);

  const loadingBarWidth = !isLoading || isWaitingForDebounce ? `0` : `${Math.min((loadingStack / maxLoadingItems) * 100, 100)}%`;

  const sidebar = bare ? null : (
    <div
      className={clsx(
        styles.sidebarContainer,
        (collapsedSidebarStatus === 'collapsing' || collapsedSidebarStatus === 'expanding') && styles.isTransitioning,
      )}
      ref={setSidebarRef}
    >
      <Sidebar />
    </div>
  );

  return (
    <div
      className={clsx(
        styles.layout,
        isMobile && styles.isMobile,
        showBanner && styles.hasBanner,
        showCollapsedSidebar && styles.hasCollapsedSidebar,
        isMobileNavExpanded && isMobile && styles.hasMobileNavOpen,
      )}
    >
      <div className={styles.bannerContainer} ref={setBannerRef}>
        <RealmPageBanner />
      </div>

      {isLoading ? <div className={styles.loader} style={{ width: loadingBarWidth }} /> : null}

      <header className={styles.topbarContainer} ref={setTopbarRef}>
        <Topbar bare={bare} mobileSidebar={isMobile ? sidebar : null} />
      </header>

      {!isMobile ? sidebar : null}

      <main className={styles.main}>
        <Suspense fallback={<FrameLoadingTrigger />}>{children}</Suspense>
      </main>
    </div>
  );
}

export function Layout({ bare, children }: PropsWithChildren<LayoutProps>) {
  const isNestedLayout = use(UniqueLayoutContext);

  if (isNestedLayout) {
    return children;
  }

  return (
    <LayoutMeasurementContextProvider>
      <LayoutContextProvider>
        <UniqueLayoutContext.Provider value={true}>
          <InnerLayout bare={bare}>{children}</InnerLayout>
        </UniqueLayoutContext.Provider>
      </LayoutContextProvider>
    </LayoutMeasurementContextProvider>
  );
}
